import { useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  BackScreenDiv,
  Button,
  ContentDiv,
  Div,
  H1,
  HeaderDiv,
  ScreenDiv,
  TableColumnDiv,
  TableRowDiv,
  UserTableBodyDiv,
  UserTableDiv,
  UserTableHeaderDiv,
  UserColumnRowDiv,
  FeedbackFormInput,
  FeedbackFormTextarea,
  P,
} from '../../../styles/styles';
import {
  saveLegalPage,
  selectLegalPageSaving,
  selectLegalPageError,
} from '../../../features/legalPages/legalPagesSlice';
import {
  LEGAL_PAGE_META,
  LEGAL_PAGE_IDS,
  LEGAL_PAGE_LOCALE,
  emptyLegalPageForm,
} from '../../../features/legalPages/legalPageConstants';

const fmt = (ts) => {
  if (!ts) return '—';
  try {
    return new Date(ts).toLocaleString();
  } catch {
    return '—';
  }
};

const readEnglishField = (map) =>
  typeof map === 'string' ? map.trim() : (map?.[LEGAL_PAGE_LOCALE] || '').trim();

export default function AdminLegalPages({ pages, loading, error, onRefresh }) {
  const dispatch = useDispatch();
  const { t } = useTranslation('registration');
  const isDesktop = useMediaQuery({ minWidth: '800px' });
  const saving = useSelector(selectLegalPageSaving);
  const saveError = useSelector(selectLegalPageError);

  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyLegalPageForm());

  const resetForm = () => {
    setEditingId(null);
    setFormData(emptyLegalPageForm());
  };

  const handleEdit = (page) => {
    setEditingId(page.id);
    setFormData({
      title: readEnglishField(page.title),
      contentHtml: readEnglishField(page.contentHtml),
    });
  };

  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSeedFromI18n = (pageId) => {
    const meta = LEGAL_PAGE_META[pageId];
    setEditingId(pageId);
    setFormData({
      title: t(meta.i18nTitleKey, { lng: LEGAL_PAGE_LOCALE }),
      contentHtml: t(meta.i18nContentKey, { lng: LEGAL_PAGE_LOCALE }),
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!editingId) return;

    const result = await dispatch(
      saveLegalPage({
        id: editingId,
        data: {
          title: { [LEGAL_PAGE_LOCALE]: formData.title },
          contentHtml: { [LEGAL_PAGE_LOCALE]: formData.contentHtml },
        },
      }),
    );
    if (result.meta.requestStatus === 'fulfilled') {
      resetForm();
      onRefresh();
    }
  };

  const editingMeta = editingId ? LEGAL_PAGE_META[editingId] : null;

  const displayPages = LEGAL_PAGE_IDS.map((id) => {
    const found = pages.find((page) => page.id === id);
    return found || { id, exists: false };
  });

  return (
    <>
      <BackScreenDiv grayImage />

      <ScreenDiv column mobileJustifySpace>
        <HeaderDiv />

        <ContentDiv
          gap={!isDesktop && '5px'}
          paddingBack={isDesktop}
          borderRadius="30px"
          alignStart
          justifyStart
          height="auto"
          heightDesktop="auto"
        >
          <Div row width="100%" justify="space-between" align="center">
            {isDesktop && <H1>Legal pages</H1>}
            <Button onClick={onRefresh} disabled={loading} width="auto" greenButton>
              {loading ? 'Loading…' : 'Refresh'}
            </Button>
          </Div>

          {error && (
            <div role="alert" style={{ color: '#c62828', marginBottom: 8 }}>
              {error}
            </div>
          )}
          {saveError && (
            <div role="alert" style={{ color: '#c62828', marginBottom: 8 }}>
              {saveError}
            </div>
          )}

          {editingId && (
            <Div
              width="100%"
              padding="20px"
              borderRadius="10px"
              backWhite
              grayBorder
              style={{ marginBottom: '20px' }}
            >
              <H1 fontSize="20px" margin="0 0 15px 0">
                Edit: {editingMeta?.label}
              </H1>
              <P fontSize="14px" textAlign="left" margin="0 0 15px 0" color="#666">
                English only — shown on all app languages.
              </P>

              <form onSubmit={handleSubmit} style={{ width: '100%' }}>
                <Div gap="15px" width="100%">
                  <Div>
                    <P fontSize="14px" textAlign="left" margin="0 0 5px 0">
                      Title
                    </P>
                    <FeedbackFormInput
                      value={formData.title}
                      onChange={(e) => handleFieldChange('title', e.target.value)}
                      placeholder="Title (English)"
                    />
                  </Div>

                  <Div>
                    <P fontSize="14px" textAlign="left" margin="0 0 5px 0">
                      Content (HTML)
                    </P>
                    <FeedbackFormTextarea
                      rows={16}
                      value={formData.contentHtml}
                      onChange={(e) => handleFieldChange('contentHtml', e.target.value)}
                      placeholder="<p>Content (English)</p>"
                      $outlined
                      style={{ minHeight: '280px' }}
                    />
                  </Div>

                  <Div row gap="10px">
                    <Button type="submit" greenButton width="auto" disabled={saving}>
                      {saving ? 'Saving...' : 'Save'}
                    </Button>
                    <Button type="button" onClick={resetForm} width="auto">
                      Cancel
                    </Button>
                  </Div>
                </Div>
              </form>
            </Div>
          )}

          <Div>
            <UserTableDiv>
              <UserTableHeaderDiv>
                <UserColumnRowDiv>Legal pages</UserColumnRowDiv>
              </UserTableHeaderDiv>

              <UserTableBodyDiv>
                {displayPages.map((page) => {
                  const meta = LEGAL_PAGE_META[page.id];
                  const status = page.exists ? 'Published in Firestore' : 'Using i18n fallback';
                  const titlePreview = page.exists
                    ? readEnglishField(page.title) || meta?.label
                    : meta?.label;

                  return (
                    <TableRowDiv key={page.id}>
                      <TableColumnDiv>{titlePreview}</TableColumnDiv>
                      <TableColumnDiv>{meta?.route}</TableColumnDiv>
                      <TableColumnDiv>{status}</TableColumnDiv>
                      <TableColumnDiv>Updated: {fmt(page.updatedAt)}</TableColumnDiv>
                      <TableColumnDiv>
                        <Div row gap="8px" wrap>
                          <Button
                            onClick={() => handleEdit(page)}
                            width="auto"
                            greenButton
                            fontSize="12px"
                            padding="5px 10px"
                          >
                            Edit
                          </Button>
                          {!page.exists && (
                            <Button
                              onClick={() => handleSeedFromI18n(page.id)}
                              width="auto"
                              outlineButton
                              fontSize="12px"
                              padding="5px 10px"
                            >
                              Load defaults
                            </Button>
                          )}
                        </Div>
                      </TableColumnDiv>
                    </TableRowDiv>
                  );
                })}
              </UserTableBodyDiv>
            </UserTableDiv>
          </Div>
        </ContentDiv>
      </ScreenDiv>
    </>
  );
}
